import { computed, defineComponent, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useSiteConfigStore } from '@/stores/site-config-store'
import { openExternal } from '@/utils/open-external'
import { ExternalAnchor } from '@/components/external-anchor'

const STORAGE_KEY = 'bottom-floating-bar-closed'

/**
 * 底部漂浮栏（跨页面固定漂浮）
 * - 整条 bar：黑色半透明背景铺满；按钮组居中（fit-content 宽度）
 * - 按钮①：白底红字「下载APP」/ ②：绿底白字（配置1）/ ③：红底白字（配置2）末尾内嵌 ×
 * - 关闭键 = 按钮③ 内的 ×；点 × 后写 localStorage，清缓存前不再出现
 */
export const BottomFloatingBar = defineComponent({
  name: 'BottomFloatingBar',
  setup() {
    const router = useRouter()
    const siteConfigStore = useSiteConfigStore()
    const visible = ref(localStorage.getItem(STORAGE_KEY) !== '1')

    const btn1 = computed(() => ({
      name: siteConfigStore.bottomBtn1Name,
      url: siteConfigStore.bottomBtn1Url,
    }))
    const btn2 = computed(() => ({
      name: siteConfigStore.bottomBtn2Name,
      url: siteConfigStore.bottomBtn2Url,
    }))

    function close() {
      visible.value = false
      localStorage.setItem(STORAGE_KEY, '1')
    }

    function handleNavigate(url: string) {
      if (!url) return
      if (/^https?:\/\//i.test(url)) openExternal(url)
      else router.push(url)
    }

    return () => {
      if (!visible.value) return null
      const hasBtn1 = !!(btn1.value.name && btn1.value.url)
      const hasBtn2 = !!(btn2.value.name && btn2.value.url)
      return (
        <div class="fixed left-0 right-0 z-[100] mx-auto max-w-[480px] bottom-[calc(50px+env(safe-area-inset-bottom))] bg-black/50 py-[0.24rem] flex justify-center">
          <div class="inline-flex items-stretch text-[0.24rem] font-bold">
            {/* 按钮①：白底 + 红字 「下载APP」 - 左侧圆角 */}
            <button
              type="button"
              class="h-[0.48rem] px-[0.28rem] bg-white text-[#c01b1b] flex items-center border-none outline-none rounded-l-[0.08rem] cursor-pointer"
              onClick={() => router.push('/download-app')}
            >
              下载APP
            </button>

            {/* 按钮②：绿底 + 白字（配置1） - 无圆角 */}
            {hasBtn1 && (
              <button
                type="button"
                class="h-[0.48rem] max-w-[2.4rem] px-[0.28rem] bg-[#059540] text-white flex items-center border-none outline-none cursor-pointer"
                onClick={() => handleNavigate(btn1.value.url)}
              >
                <span class="truncate">{btn1.value.name}</span>
              </button>
            )}

            {/* 按钮③：红底 + 白字（配置2）+ 末尾内嵌 × - 右侧圆角 */}
            {/* 文字是跳转（站外走 ExternalAnchor），× 是关闭，两者拆开，不再靠 stopPropagation */}
            <div class="h-[0.48rem] max-w-[2.4rem] pl-[0.28rem] pr-[0.16rem] bg-[#c01b1b] text-white flex items-center rounded-r-[0.08rem] cursor-pointer">
              {hasBtn2 &&
                (/^https?:\/\//i.test(btn2.value.url) ? (
                  <ExternalAnchor class="truncate" url={btn2.value.url}>
                    {btn2.value.name}
                  </ExternalAnchor>
                ) : (
                  <RouterLink class="btn-reset truncate" to={btn2.value.url}>
                    {btn2.value.name}
                  </RouterLink>
                ))}
              <button
                type="button"
                class="btn-reset ml-[0.1rem] shrink-0 w-[0.32rem] h-[0.32rem] flex items-center justify-center cursor-pointer"
                aria-label="关闭"
                onClick={close}
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )
    }
  },
})

export default BottomFloatingBar
